export function buildInitialValues(form) {
    const values = {};

    (form?.sections || []).forEach(section => {
        (section?.fields || []).forEach(field => {
            values[field.id] = "";
        });
    });

    return values;
}

const ALWAYS_DISABLED_IDS = new Set([
    // Revisions
    "cu_app1_reqBy",
    "cu_app2_reqBy",

    // CU1
    "cu1_reqBy",
    "cu1_reviewedBy",

    // CU2
    "cu2_reqBy",
    "cu2_reviewedBy",

    "cu_app1_revisionHistory",
    "cu_app2_revisionHistory"
]);

export function Control({
    field,
    value,
    onChange,
    sectionDisabled,
    pageReadOnly = false,
    fieldForceReadOnly = false
}) {
    const rowHeight = "h-7";

    const isAlwaysDisabled =
        field?.isUpdate === false ||
        field?.readonly === true ||
        ALWAYS_DISABLED_IDS.has(field?.id);

    const disabled =
        isAlwaysDisabled ||
        fieldForceReadOnly ||
        sectionDisabled ||
        pageReadOnly;

    const baseCls =
        `form-input w-full text-sm border rounded p-1 ${rowHeight}`;

    if (field.control === "spacer") {
        return <div className={rowHeight} />;
    }

    if (field.control === "label") {
        return (
            <div className={`flex items-center text-sm text-gray-600 ${rowHeight}`}>
                {field.note || ""}
            </div>
        );
    }

    if (field.control === "textarea") {
        const textareaHeight = field.height ? `${field.height}` : "72px";

        return (
            <textarea
                id={field.encompassId}
                className={`form-input w-full text-sm border rounded p-1 resize-none`}
                style={{ height: textareaHeight }}
                value={value || ""}
                disabled={disabled}
                onChange={(e) => onChange(field.id, e.target.value)}
            />
        );
    }

    if (field.control === "dropdown") {
        const opts = (field.options || []).map(o =>
            typeof o === "string" ? { text: o, value: o } : o
        );

        const dropdownValue =
            field.id === "cu_fcd_status" && !value
                ? "Decision Pending"
                : (value || "");

        return (
            <select
                id={field.encompassId}
                className={baseCls}
                value={dropdownValue}
                onChange={(e) => onChange(field.id, e.target.value)}
                disabled={disabled}
            >
                <option value=""></option>
                {opts.map(o => (
                    <option key={o.value} value={o.value}>
                        {o.text}
                    </option>
                ))}
            </select>
        );
    }

    if (field.control === "radio") {
        return (
            <div className={`flex items-center gap-4 ${rowHeight}`}>
                {(field.options || ["Yes", "No"]).map(o => (
                    <label key={o} className="flex items-center gap-2 text-sm">
                        <input
                            e-id={field.encompassId}
                            type="radio"
                            disabled={disabled}
                            checked={value === o}
                            onChange={() => onChange(field.id, o)}
                        />
                        {o}
                    </label>
                ))}
            </div>
        );
    }

    if (field.control === "decimal") {
        const stripCommas = (s) =>
            s == null ? "" : String(s).replace(/,/g, "").trim();

        const formatCurrency = (s) => {
            const raw = stripCommas(s);
            if (!raw) return "";

            const num = Number(raw);
            if (Number.isNaN(num)) return s;

            return num.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        };

        const handleChange = (e) => {
            const val = e.target.value;
            const raw = stripCommas(val);

            if (/^\d*\.?\d{0,2}$/.test(raw)) {
                onChange(field.id, raw);
            }
        };

        const handleBlur = () => {
            if (!value) return;

            const formatted = formatCurrency(value);
            onChange(field.id, formatted);
        };

        return (
            <input
                id={field.encompassId}
                type="text"
                inputMode="decimal"
                disabled={disabled}
                className={`${baseCls} ${disabled ? "field-disabled" : ""}`}
                value={value || ""}
                onChange={handleChange}
                onBlur={handleBlur}
            />
        );
    }

    const type =
        field.control === "number" ? "number" :
            field.control === "date" ? "date" :
                field.control === "datetime" ? "datetime-local" :
                    "text";

    return (
        <input
            id={field.encompassId}
            type={type}
            className={`${baseCls} ${disabled ? "field-disabled" : ""}`}
            disabled={disabled}
            value={value || ""}
            onChange={(e) => onChange(field.id, e.target.value)}
        />
    );
}


export function ExcelRow({
    field,
    value,
    onChange,
    sectionDisabled,
    pageReadOnly = false,
    fieldForceReadOnly = false
}) {
    if (field.control === "fullspacer") {
        return (
            <div className="grid grid-cols-[101%] py-[2px]">
                <div
                    style={{
                        width: "100%",
                        ...field.style
                    }}
                />
            </div>
        );
    }

    const isTextarea = field.control === "textarea";
    const isFullWidth = field.fullWidth || isTextarea;
    const isAlgoField = (field?.id || "").startsWith("cu_algo_");

    const labelWrapCls = isAlgoField
        ? "cashtoclose-label bg-[#4EA72E] !text-white font-semibold rounded-sm px-2 py-1"
        : "cashtoclose-label";

    const labelTextCls = isAlgoField
        ? "form-label !text-white"
        : "form-label";

    return (
        <div
            className={
                isFullWidth
                    ? "grid grid-cols-[100%] items-center py-[2px] pl-[4px]"
                    : "grid grid-cols-[50%_10%_40%] gap-1 items-center py-[2px] px-[4px]"
            }
        >
            <div
                className={labelWrapCls}
                style={{
                    alignSelf: isFullWidth ? "flex-start" : "center",
                }}
            >
                <label className={labelTextCls}>{field.label}</label>
                {field.note && (
                    <div className={`text-xs ${isAlgoField ? "text-white/90" : "text-gray-500"}`}>
                        {field.note}
                    </div>
                )}
            </div>

            {!isFullWidth && <div className="cashtoclose-control"></div>}

            <div className="cashtoclose-control">
                <Control
                    field={field}
                    value={value}
                    onChange={onChange}
                    sectionDisabled={sectionDisabled}
                    pageReadOnly={pageReadOnly}
                    fieldForceReadOnly={fieldForceReadOnly}
                />
            </div>
        </div>
    );
}

export const HISTORY_ENCOMPASS_ID = "CX.CUREVIEW.HISTORY";
export const HISTORY_UI_ID = "cu_History";

export const isoToMdy = (iso) => {
    if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso || "";
    const [y, m, d] = iso.split("-");
    return `${Number(m)}/${Number(d)}/${y}`;
};

// ---------- history parse/stringify ----------
export const parseCuHistory = (raw) => {
    if (!raw) return [];
    const s = String(raw).trim();
    if (!s) return [];

    try {
        const parsed = JSON.parse(s);
        if (Array.isArray(parsed)) return parsed;
        // allow single object stored
        if (parsed && typeof parsed === "object") return [parsed];
        return [];
    } catch {
        // if old loans had non-json text, ignore safely
        return [];
    }
};

export const stringifyCuHistory = (arr) => JSON.stringify(arr ?? []);

// "same as last" duplicate detection
// Find the last entry for the SAME CU type, then compare
export const isSameAsLastForCuType = (historyArr, entry) => {
    if (!Array.isArray(historyArr) || !historyArr.length) return false;

    // find last row for same CU type (CU1 or CU2)
    for (let i = historyArr.length - 1; i >= 0; i--) {
        const last = historyArr[i];
        if (String(last?.cuType || "") !== String(entry?.cuType || "")) continue;

        return (
            String(last.actionDate || "") === String(entry.actionDate || "") &&
            String(last.cuType || "") === String(entry.cuType || "") &&
            String(last.actionType || "") === String(entry.actionType || "") &&
            String(last.requestType || "") === String(entry.requestType || "")
        );
    }

    return false;
};

const getCurrentDateTime = () => {
    const now = new Date();

    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const yyyy = now.getFullYear();

    const hh = String(now.getHours()).padStart(2, "0");
    const min = String(now.getMinutes()).padStart(2, "0");
    const sec = String(now.getSeconds()).padStart(2, "0");

    return `${mm}/${dd}/${yyyy} ${hh}:${min}:${sec}`;
};


export const buildHistoryEntry = ({
    cuType,
    actionType,
    actionBy,
    requestType,
    actionDateIso,
    decision,
    algoReasons,
    requestNote,
    reviewNote
}) => {
    const e = {
        createdAt: getCurrentDateTime(),
        actionDate: actionDateIso,
        cuType,
        actionType,
        actionBy,
        requestType,
        decision: decision || "",
        algoReasons: algoReasons || []
    };

    if ((requestNote || "").trim()) e.requestNote = String(requestNote).trim();
    if ((reviewNote || "").trim()) e.reviewNote = String(reviewNote).trim();

    return e;
};


// Normalize any date to ISO yyyy-mm-dd so comparisons are stable.
// Accepts: "2026-03-02" OR "3/2/2026" OR "03/02/2026"
const toIsoDate = (s) => {
    if (s == null) return "";
    const str = String(s).trim();
    if (!str) return "";

    // already ISO?
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;

    // M/D/YYYY or MM/DD/YYYY
    const m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m) {
        const mm = String(m[1]).padStart(2, "0");
        const dd = String(m[2]).padStart(2, "0");
        const yyyy = m[3];
        return `${yyyy}-${mm}-${dd}`;
    }

    // unknown format - return trimmed string (still better than crashing)
    return str;
};

export const datetimeLocalToMdyHm = (s) => {
    // input: "YYYY-MM-DDTHH:mm"
    if (!s) return "";
    const str = String(s).trim();
    const m = str.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
    if (!m) return str;
    const [, yyyy, mm, dd, hh, min] = m;
    return `${Number(mm)}/${Number(dd)}/${yyyy} ${hh}:${min}`;
};

export const mdyHmToDatetimeLocal = (s) => {
    // accepts "M/D/YYYY H:mm" or "MM/DD/YYYY HH:mm" (seconds optional)
    if (!s) return "";
    const str = String(s).trim();

    // already datetime-local?
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(str)) return str;

    const m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}))?/);
    if (!m) return "";

    const mm = String(m[1]).padStart(2, "0");
    const dd = String(m[2]).padStart(2, "0");
    const yyyy = m[3];
    const hh = String(m[4] ?? "00").padStart(2, "0");
    const min = String(m[5] ?? "00").padStart(2, "0");

    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
};


export const CU1_CLEAR_FIELD_IDS = [
    "cu1_reqDate",
    "cu1_reviewedDate",
    "cu1_reqType",
    "cu1_reviewDecision",
    "cu1_reqBy",
    "cu1_reviewedBy",
    "cu1_reqNote",
    "cu1_reviewNote",
];

export const CU2_CLEAR_FIELD_IDS = [
    "cu2_reqDate",
    "cu2_reviewedDate",
    "cu2_reqType",
    "cu2_reviewDecision",
    "cu2_reqBy",
    "cu2_reviewedBy",
    "cu2_reqNote",
    "cu2_reviewNote",
];

export const APP1_REVISION_CLEAR_FIELD_IDS = [
    "cu_app1_revisionType",
    "cu_app1_discoveryDate",
    "cu_app1_reqDate",
    "cu_app1_reqBy"
];

export const APP2_REVISION_CLEAR_FIELD_IDS = [
    "cu_app2_revisionType",
    "cu_app2_discoveryDate",
    "cu_app2_reqDate",
    "cu_app2_reqBy"
];

// Pure helpers for Final Collateral Determination audit entries.
const comparableValue = (field, value, normalize) => {
    const normalized = String(normalize(field, value) ?? "");
    if (field.control === "decimal" && normalized !== "" && Number.isFinite(Number(normalized))) {
        return String(Number(normalized));
    }
    return normalized;
};

export const buildFcdHistoryEntry = (fields, previousValues, nextValues, actionBy, normalize, skipEmptyDates = false, now = new Date()) => {
    if (!previousValues) return null;

    // Match the values that will actually be saved, including skipped empty dates.
    const finalCuValue = String(nextValues.cu_fcd_finalCuValue ?? "").trim();
    const reviewDate = String(nextValues.cu_fcd_reviewerDate ?? "").trim();
    const effectiveReviewDate = skipEmptyDates && !reviewDate ? String(previousValues.cu_fcd_reviewerDate ?? "").trim() : reviewDate;
    const canLogStatus = Boolean(finalCuValue || effectiveReviewDate);
    const changes = [];
    for (const field of fields) {
        if (!field.encompassId || field.isUpdate === false || field.id === "cu_fcd_reviewerName") continue;
        if (field.id === "cu_fcd_status" && !canLogStatus) continue;
        const oldValue = comparableValue(field, previousValues[field.id], normalize);
        const newValue = comparableValue(field, nextValues[field.id], normalize);
        if (skipEmptyDates && field.control === "date" && newValue === "") continue;
        if (oldValue === newValue) continue;
        changes.push({ fieldId: field.encompassId, label: field.label || field.id, oldValue, newValue });
    }
    if (!changes.length) return null;

    const pad = value => String(value).padStart(2, "0");
    const actionDate = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    return {
        createdAt: isoToMdy(actionDate),
        actionDate,
        cuType: "FCD",
        actionType: "Updated",
        actionBy: String(actionBy || "").trim(),
        requestType: "Final Collateral Determination",
        decision: String(nextValues.cu_fcd_status || "").trim(),
        changes
    };
};

export const formatFcdHistoryEntry = (entry) => {
    const isBlank = value => value == null || String(value).trim() === "";
    const display = value => isBlank(value) ? "(blank)" : String(value);
    const details = (entry.changes || []).filter(change => change.fieldId !== "CX.APPR.FCD.REV.NAME" && change.fieldId !== "cu_fcd_reviewerName" && String(change.label || "").trim() !== "Reviewer Name").map(change => {
        const newText = `"${display(change.newValue)}"`;
        return `${change.label}: ${isBlank(change.oldValue) ? newText : `"${display(change.oldValue)}" → ${newText}`}`;
    }).join(" | ");
    // Strip time from older entries as well; match the CU1/CU2 date display.
    const dateOnly = String(entry.actionDate || entry.createdAt || "").trim().split(/[T\s]/)[0];
    const date = isoToMdy(toIsoDate(dateOnly));
    return `${date} Final Collateral Determination updated by ${entry.actionBy || "Unknown user"}${details ? ` | ${details}` : ""}`;
};
